import { useEffect, useState } from "react";
import DB_PATH from "../DB_PATH";
import { firestore } from "../firebase";

export type SitePageSummary = {
  pageKey: string;
  title?: string;
  navLabel?: string;
  order?: number;
  enabled?: boolean;
  updatedAt?: string;
};

export type SiteConfigDoc = {
  enabled?: boolean;
  showArticlesInNav?: boolean;
};

const siteConfigRef = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection("landingPage")
    .doc("site");

export const useSiteConfig = ({ communityId }: { communityId?: string }) => {
  const [config, setConfig] = useState<SiteConfigDoc | null>(null);
  useEffect(() => {
    if (!communityId) {
      setConfig(null);
      return;
    }
    const unsub = siteConfigRef(communityId).onSnapshot(
      (doc) => setConfig(doc.exists ? (doc.data() as SiteConfigDoc) : null),
      () => setConfig(null)
    );
    return () => unsub();
  }, [communityId]);
  return config;
};

export const useSitePages = ({ communityId }: { communityId?: string }) => {
  const [pages, setPages] = useState<SitePageSummary[]>([]);
  useEffect(() => {
    if (!communityId) {
      setPages([]);
      return;
    }
    const unsub = firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection("sitePages")
      .onSnapshot(
        (snap) => {
          const rows = snap.docs
            .map((d) => {
              const data = d.data() as any;
              return {
                pageKey: d.id,
                title: data.title,
                navLabel: data.navLabel,
                order: data.order,
                enabled: data.enabled,
                updatedAt: data.updatedAt,
              } as SitePageSummary;
            })
            .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
          setPages(rows);
        },
        () => setPages([])
      );
    return () => unsub();
  }, [communityId]);
  return pages;
};

// Toggle website mode. A Firestore trigger republishes the public projection.
export const setSiteEnabled = async (
  communityId: string,
  enabled: boolean
): Promise<void> => {
  await siteConfigRef(communityId).set(
    { enabled, updatedAt: new Date().toISOString() },
    { merge: true }
  );
};
